import React, { useState } from 'react';
import WorkflowTabView from '../../../components/WorkflowTabView';
import { Users, Send, FileCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const SignOffWorkflow = ({ previewOnly = false }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [approvers] = useState([]);
  const [routingType, setRoutingType] = useState('parallel');
  const [requestsSent, setRequestsSent] = useState(false);
  const [allSigned] = useState(false);

  const steps = [
    {
      id: 1,
      title: t('workflow.signOff.steps.approvers.title'),
      description: t('workflow.signOff.steps.approvers.description'),
      icon: Users
    },
    {
      id: 2,
      title: t('workflow.signOff.steps.sendTrack.title'),
      description: t('workflow.signOff.steps.sendTrack.description'),
      icon: Send
    },
    {
      id: 3,
      title: t('workflow.signOff.steps.protocol.title'),
      description: t('workflow.signOff.steps.protocol.description'),
      icon: FileCheck
    }
  ];

  const handleStepChange = (stepId) => {
    setCurrentStep(stepId);
  };

  const renderApproversTab = () => (
    <div className="space-y-6">
      {/* Header Chips */}
      <div className="flex flex-wrap gap-2 mb-6">
        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
          Draft: v1 (locked)
        </span>
        <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
          Approvers: {approvers.length}/—
        </span>
        <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
          Requests sent: {requestsSent ? '✓' : '—'}
        </span>
        <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
          All signed: {allSigned ? '✓' : '—'}
        </span>
      </div>

      {/* Routing Type */}
      <div className="bg-white p-4 rounded-lg border">
        <h3 className="text-lg font-semibold mb-4">Routing</h3>
        <div className="flex space-x-4">
          <label className="flex items-center">
            <input
              type="radio"
              value="parallel"
              checked={routingType === 'parallel'}
              onChange={(e) => setRoutingType(e.target.value)}
              className="mr-2"
            />
            Parallel
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              value="sequential"
              checked={routingType === 'sequential'}
              onChange={(e) => setRoutingType(e.target.value)}
              className="mr-2"
            />
            Sequential
          </label>
        </div>
      </div>

      {/* Approvers Table */}
      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">Approvers</h3>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
            Add approver
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Role</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Deadline</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {approvers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                    No approvers added yet
                  </td>
                </tr>
              ) : (
                approvers.map((approver, index) => (
                  <tr key={index}>
                    <td className="px-4 py-3 text-sm text-gray-900">{approver.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{approver.role}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        approver.status === 'Signed' ? 'bg-green-100 text-green-800' :
                        approver.status === 'Requested' ? 'bg-yellow-100 text-yellow-800' :
                        approver.status === 'Changes' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {approver.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{approver.deadline}</td>
                    <td className="px-4 py-3 text-sm">
                      <button className="text-red-600 hover:text-red-800">Remove</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderSendTrackTab = () => (
    <div className="space-y-6">
      {/* Header Chips */}
      <div className="flex flex-wrap gap-2 mb-6">
        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
          Draft: v1 (locked)
        </span>
        <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
          Approvers: {approvers.length}/—
        </span>
        <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
          Requests sent: {requestsSent ? '✓' : '—'}
        </span>
        <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
          All signed: {allSigned ? '✓' : '—'}
        </span>
      </div>

      {/* Send Requests */}
      <div className="bg-white p-6 rounded-lg border">
        <h3 className="text-lg font-semibold mb-4">Send & Track</h3>
        <button 
          className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium"
          onClick={() => setRequestsSent(true)}
        >
          Send requests
        </button>
        <p className="text-sm text-gray-600 mt-2">
          Sends emails/links to preview and approve draft
        </p>
      </div>

      {/* Approvers Status */}
      {approvers.length > 0 && (
        <div className="bg-white rounded-lg border">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold">Approval Status</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Name</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {approvers.map((approver, index) => (
                  <tr key={index}>
                    <td className="px-4 py-3 text-sm text-gray-900">{approver.name}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        approver.status === 'Signed' ? 'bg-green-100 text-green-800' :
                        approver.status === 'Requested' ? 'bg-yellow-100 text-yellow-800' :
                        approver.status === 'Changes' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {approver.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm space-x-2">
                      <button className="text-blue-600 hover:text-blue-800">Resend</button>
                      <button className="text-red-600 hover:text-red-800">Recall</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Attachments */}
      <div className="bg-white rounded-lg border">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Attachments (Read-only)</h3>
        </div>
        <div className="p-4">
          <div className="space-y-2">
            <div className="flex items-center text-sm text-gray-600">
              <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
              report_v1_draft.pdf
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
              Methodology
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
              DMA snapshot
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
              Coverage summary
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
              Assumptions (Approved)
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProtocolTab = () => (
    <div className="space-y-6">
      {/* Header Chips */}
      <div className="flex flex-wrap gap-2 mb-6">
        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
          Draft: v1 (locked)
        </span>
        <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
          Approvers: {approvers.length}/—
        </span>
        <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
          Requests sent: {requestsSent ? '✓' : '—'}
        </span>
        <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
          All signed: {allSigned ? '✓' : '—'}
        </span>
      </div>

      {/* Protocol Generation */}
      <div className="bg-white p-6 rounded-lg border">
        <h3 className="text-lg font-semibold mb-4">Protocol</h3>
        {allSigned ? (
          <button className="px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium">
            Generate protocol (PDF)
          </button>
        ) : (
          <div className="text-gray-600">
            <p>All required approvers must sign before generating protocol.</p>
            <button 
              className="px-6 py-3 bg-gray-300 text-gray-500 rounded-md cursor-not-allowed font-medium mt-2"
              disabled
            >
              Generate protocol (PDF)
            </button>
          </div>
        )}
      </div>

      {/* Signer Details */}
      {approvers.length > 0 && (
        <div className="bg-white rounded-lg border">
          <div className="p-4 border-b">
            <h3 className="text-lg font-semibold">Signer Details</h3>
          </div>
          <div className="p-4">
            <div className="space-y-4">
              {approvers.map((approver, index) => (
                <div key={index} className="border-b pb-4 last:border-b-0">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-900">Name:</span>
                      <span className="ml-2 text-gray-600">{approver.name}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-900">Role:</span>
                      <span className="ml-2 text-gray-600">{approver.role}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-900">Timestamp:</span>
                      <span className="ml-2 text-gray-600">
                        {approver.status === 'Signed' ? '2024-01-15 14:30:25' : '—'}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-900">Document Hash:</span>
                      <span className="ml-2 text-gray-600 font-mono text-xs">
                        {approver.status === 'Signed' ? 'a1b2c3d4e5f6...' : '—'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderApproversTab();
      case 2:
        return renderSendTrackTab();
      case 3:
        return renderProtocolTab();
      default:
        return renderApproversTab();
    }
  };

  return (
    <WorkflowTabView
      steps={steps}
        currentStep={currentStep} 
      onStepChange={handleStepChange}
      maxWidth="max-w-7xl"
      previewOnly={previewOnly}
    >
        {renderCurrentStep()}
    </WorkflowTabView>
  );
};

export default SignOffWorkflow;
